import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Banner,
  MonoLabel,
  Skeleton
} from '../components/primitives';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import {
  SettingsWorkItemsTabs,
  type SettingsWorkItemsTab
} from '../components/settings/SettingsWorkItemsTabs';
import { supabase } from '../lib/supabase';
import { useActiveOrg } from '../lib/activeOrg';
import './SettingsWorkItems.css';

type WorkItemType = 'epic' | 'story' | 'bug' | 'task' | 'subtask';

interface PipelineRow {
  id: string;
  org_id: string;
  applies_to_type: WorkItemType | null;
  name: string;
  stages: string[];
  read_only: boolean;
}

interface PropertyDefRow {
  id: string;
  type_id: string | null;
  key: string;
  label: string;
  kind: string;
  position: number;
}

interface PropertyOptionRow {
  id: string;
  property_def_id: string;
  label: string;
  position: number;
}

type LoadState = 'loading' | 'ready' | 'error' | 'unauth' | 'empty';

interface TypeCard {
  type: WorkItemType;
  label: string;
  meta: string;
  defaultProps: string[];
  pipelineLabel: string;
}

const TYPE_CARDS: TypeCard[] = [
  {
    type: 'epic',
    label: 'Epic',
    meta: 'Top-level container. Has its own scoped board.',
    defaultProps: ['Priority', 'Owner team', 'Target release'],
    pipelineLabel: 'Epic pipeline ↗'
  },
  {
    type: 'story',
    label: 'Story',
    meta: 'Unit of value delivery. Lives on the workspace board.',
    defaultProps: ['Priority', 'Assignee', 'Estimate', 'Sprint', 'Due'],
    pipelineLabel: 'Shared (Story / Bug / Task) ↗'
  },
  {
    type: 'bug',
    label: 'Bug',
    meta: 'Defect. May be split into its own pipeline if Bug-specific stages are needed.',
    defaultProps: ['Priority', 'Severity', 'Assignee', 'Reported by'],
    pipelineLabel: 'Shared (Story / Bug / Task) ↗'
  },
  {
    type: 'task',
    label: 'Task',
    meta: "Catch-all unit of work that doesn't fit Story or Bug.",
    defaultProps: ['Assignee', 'Due'],
    pipelineLabel: 'Shared (Story / Bug / Task) ↗'
  },
  {
    type: 'subtask',
    label: 'Subtask',
    meta: 'Checklist-grade child of a Story / Bug / Task.',
    defaultProps: ['Assignee'],
    pipelineLabel: 'Subtask (fixed — To do / Done) ↗'
  }
];

function parseStages(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((s) => String(s));
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map((s) => String(s)) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function propertyKey(label: string): string {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);
  return base.length >= 2 ? base : `prop_${Date.now().toString(36).slice(-6)}`;
}

function uniqueKey(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

export function SettingsWorkItems() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: SettingsWorkItemsTab =
    tabParam === 'pipelines'
      ? 'pipelines'
      : tabParam === 'property-library'
        ? 'property-library'
        : 'types';

  // The active org is owned by the shell (see `useActiveOrg`). This page is a
  // pure consumer: it scopes every query to whatever org the shell says is
  // active and re-loads when the user switches. It must NOT re-derive its own
  // "oldest membership" org — that was the cross-org leak.
  const { activeOrgId } = useActiveOrg();
  const [state, setState] = useState<LoadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [pipelines, setPipelines] = useState<ReadonlyArray<PipelineRow>>([]);
  const [propertyDefs, setPropertyDefs] = useState<
    ReadonlyArray<PropertyDefRow>
  >([]);
  const [propertyOptions, setPropertyOptions] = useState<
    ReadonlyArray<PropertyOptionRow>
  >([]);
  const [splitPending, setSplitPending] = useState(false);
  const [addPropPending, setAddPropPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState('loading');
      setError(null);

      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setState('unauth');
        return;
      }

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!activeOrgId) return;

      const [pipeRes, defRes, optRes] = await Promise.all([
        supabase
          .from('work_item_status_pipelines')
          .select('id, org_id, applies_to_type, name, stages, read_only')
          .eq('org_id', activeOrgId),
        supabase
          .from('work_item_property_defs')
          .select('id, type_id, key, label, kind, position')
          .eq('org_id', activeOrgId)
          .is('archived_at', null)
          .order('position', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase
          .from('work_item_property_options')
          .select('id, property_def_id, label, position')
          .eq('org_id', activeOrgId)
          .is('archived_at', null)
          .order('position', { ascending: true })
      ]);
      if (cancelled) return;
      if (pipeRes.error) {
        setError(pipeRes.error.message);
        setState('error');
        return;
      }
      // Property reads may fail under stricter RLS for `options`
      // (manager-gated). Treat as empty rather than fatal — the page must
      // still render Types/Pipelines tabs even if a member can't see options.
      setPipelines(
        (pipeRes.data ?? []).map((r) => ({
          id: r.id as string,
          org_id: r.org_id as string,
          applies_to_type: r.applies_to_type as WorkItemType | null,
          name: r.name as string,
          stages: parseStages(r.stages),
          read_only: Boolean(r.read_only)
        }))
      );
      setPropertyDefs(
        defRes.error ? [] : ((defRes.data ?? []) as PropertyDefRow[])
      );
      setPropertyOptions(
        optRes.error ? [] : ((optRes.data ?? []) as PropertyOptionRow[])
      );
      setState('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  const setActiveTab = useCallback(
    (next: SettingsWorkItemsTab) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'types') params.delete('tab');
      else params.set('tab', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const pipelineByType = useMemo(() => {
    const map = new Map<WorkItemType | 'shared', PipelineRow>();
    for (const p of pipelines) {
      if (p.applies_to_type === null) map.set('shared', p);
      else map.set(p.applies_to_type, p);
    }
    return map;
  }, [pipelines]);

  const optionsByDefId = useMemo(() => {
    const map = new Map<string, PropertyOptionRow[]>();
    for (const opt of propertyOptions) {
      const list = map.get(opt.property_def_id);
      if (list) list.push(opt);
      else map.set(opt.property_def_id, [opt]);
    }
    return map;
  }, [propertyOptions]);

  const epicPipe = pipelineByType.get('epic') ?? null;
  const sharedPipe = pipelineByType.get('shared') ?? null;
  const bugPipe = pipelineByType.get('bug') ?? null;
  const subtaskPipe = pipelineByType.get('subtask') ?? null;
  const bugSplit = bugPipe !== null;

  const handleSplitBug = useCallback(async () => {
    if (!activeOrgId || !sharedPipe || bugSplit || splitPending) return;
    setSplitPending(true);
    const { data, error: insErr } = await supabase
      .from('work_item_status_pipelines')
      .insert({
        org_id: activeOrgId,
        applies_to_type: 'bug',
        name: 'Bug',
        stages: sharedPipe.stages,
        read_only: false
      })
      .select('id, org_id, applies_to_type, name, stages, read_only')
      .single();
    setSplitPending(false);
    if (insErr || !data) {
      setError(insErr?.message ?? 'Could not split Bug pipeline');
      return;
    }
    setPipelines((prev) => [
      ...prev,
      {
        id: data.id as string,
        org_id: data.org_id as string,
        applies_to_type: 'bug',
        name: data.name as string,
        stages: parseStages(data.stages),
        read_only: Boolean(data.read_only)
      }
    ]);
  }, [activeOrgId, sharedPipe, bugSplit, splitPending]);

  const handleMergeBug = useCallback(async () => {
    if (!activeOrgId || !bugPipe || splitPending) return;
    setSplitPending(true);
    const { error: delErr } = await supabase
      .from('work_item_status_pipelines')
      .delete()
      .eq('id', bugPipe.id);
    setSplitPending(false);
    if (delErr) {
      setError(delErr.message);
      return;
    }
    setPipelines((prev) => prev.filter((p) => p.id !== bugPipe.id));
  }, [activeOrgId, bugPipe, splitPending]);

  const handleAddProperty = useCallback(async () => {
    if (!activeOrgId || addPropPending) return;
    setAddPropPending(true);
    const label = 'New property';
    const takenKeys = new Set(propertyDefs.map((p) => p.key));
    const key = uniqueKey(propertyKey(label), takenKeys);
    const position =
      propertyDefs.reduce((max, p) => Math.max(max, p.position), -1) + 1;
    const { data, error: insErr } = await supabase
      .from('work_item_property_defs')
      .insert({
        org_id: activeOrgId,
        type_id: null,
        key,
        kind: 'text',
        label,
        position,
        visible_in_list: false
      })
      .select('id, type_id, key, label, kind, position')
      .single();
    setAddPropPending(false);
    if (insErr || !data) {
      setError(insErr?.message ?? 'Could not create property');
      return;
    }
    setPropertyDefs((prev) => [...prev, data as PropertyDefRow]);
  }, [activeOrgId, propertyDefs, addPropPending]);

  return (
    <section
      className="set-wi-page"
      data-testid="settings-work-items"
      data-status={state}
      data-active-tab={activeTab}
      data-bug-split={bugSplit ? 'true' : 'false'}
    >
      <SettingsBackLink />

      <header className="set-wi-header" data-testid="settings-work-items-header">
        <h1 className="set-wi-title">Work Item settings</h1>
        <MonoLabel size="sm" tracking="wide" tone="muted">
          Work Items
        </MonoLabel>
      </header>
      <p className="set-wi-sub">
        Types, Property library, and status pipelines for Work Items. Types are a
        fixed enum (Epic / Story / Bug / Task / Subtask). Properties are
        workspace-scoped. Each type has its own status pipeline.
      </p>

      <SettingsWorkItemsTabs active={activeTab} onChange={setActiveTab} />

      {state === 'loading' ? (
        <div className="set-wi-loading">
          <Skeleton height={48} />
          <Skeleton height={320} />
        </div>
      ) : null}
      {state === 'unauth' ? (
        <Banner kind="default">Sign in to view work item settings.</Banner>
      ) : null}
      {state === 'empty' ? (
        <Banner kind="default">You don't belong to any org yet.</Banner>
      ) : null}
      {state === 'error' ? (
        <Banner kind="error" data-testid="settings-work-items-error">
          {error ?? "Couldn't load work item settings. Try again."}
        </Banner>
      ) : null}

      {state === 'ready' && activeTab === 'types' ? (
        <TypesPane onGotoPipelines={() => setActiveTab('pipelines')} />
      ) : null}

      {state === 'ready' && activeTab === 'pipelines' ? (
        <PipelinesPane
          epic={epicPipe}
          shared={sharedPipe}
          bug={bugPipe}
          subtask={subtaskPipe}
          bugSplit={bugSplit}
          splitPending={splitPending}
          onSplit={() => void handleSplitBug()}
          onMerge={() => void handleMergeBug()}
        />
      ) : null}

      {state === 'ready' && activeTab === 'property-library' ? (
        <PropertyLibraryPane
          propertyDefs={propertyDefs}
          optionsByDefId={optionsByDefId}
          addPending={addPropPending}
          onAdd={() => void handleAddProperty()}
        />
      ) : null}
    </section>
  );
}

function TypesPane({
  onGotoPipelines
}: {
  onGotoPipelines: () => void;
}) {
  return (
    <div
      className="set-wi-pane set-wi-pane--types"
      data-testid="settings-work-items-pane-types"
    >
      <div className="set-wi-hier" data-testid="settings-work-items-hierarchy">
        <h2 className="set-wi-hier-title">
          Hierarchy (structural — not editable)
        </h2>
        <div className="set-wi-hier-row set-wi-hier-row--l1">
          <span className="set-wi-type-pill">Epic</span>
          <span className="set-wi-hier-note">root only — never a child</span>
        </div>
        <div className="set-wi-hier-row set-wi-hier-row--l2">
          <span className="set-wi-hier-conn">└──</span>
          <span className="set-wi-type-pill">Story</span>
          <span className="set-wi-type-pill">Bug</span>
          <span className="set-wi-type-pill">Task</span>
          <span className="set-wi-hier-note">
            root OR child of Epic — never child of each other
          </span>
        </div>
        <div className="set-wi-hier-row set-wi-hier-row--l3">
          <span className="set-wi-hier-conn">└──</span>
          <span className="set-wi-type-pill">Subtask</span>
          <span className="set-wi-hier-note">
            must have parent at level 2 — never has children
          </span>
        </div>
        <p className="set-wi-locked">
          <strong>Type is locked at creation.</strong> A Story is never promoted
          to an Epic; an Epic is never demoted to a Story. If miscategorized,
          create the correct item, copy content, archive the wrong one.{' '}
          <strong>No "Test" type</strong> — test plans are a Document type;
          individual test cases are Stories/Tasks with a Test tag.
        </p>
      </div>

      <div className="set-wi-grid" data-testid="settings-work-items-type-grid">
        {TYPE_CARDS.map((card) => (
          <article
            key={card.type}
            className="set-wi-card"
            data-testid={`settings-work-items-type-card-${card.type}`}
          >
            <header className="set-wi-card-head">
              <span className="set-wi-type-pill">{card.label}</span>
            </header>
            <p className="set-wi-card-meta">{card.meta}</p>
            <div className="set-wi-card-lbl">Default ✦ properties</div>
            <div className="set-wi-card-chips">
              {card.defaultProps.map((p) => (
                <span key={p} className="set-wi-chip">
                  {p}
                </span>
              ))}
            </div>
            <div className="set-wi-card-lbl">Status pipeline</div>
            <div className="set-wi-card-chips">
              <button
                type="button"
                className="set-wi-chip set-wi-chip--link"
                data-testid={`settings-work-items-type-card-${card.type}-pipeline-chip`}
                onClick={onGotoPipelines}
              >
                {card.pipelineLabel}
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function PipelinesPane({
  epic,
  shared,
  bug,
  subtask,
  bugSplit,
  splitPending,
  onSplit,
  onMerge
}: {
  epic: PipelineRow | null;
  shared: PipelineRow | null;
  bug: PipelineRow | null;
  subtask: PipelineRow | null;
  bugSplit: boolean;
  splitPending: boolean;
  onSplit: () => void;
  onMerge: () => void;
}) {
  return (
    <div
      className="set-wi-pane set-wi-pane--pipes"
      data-testid="settings-work-items-pane-pipelines"
    >
      {epic ? (
        <PipelineCard
          testId="settings-work-items-pipeline-epic"
          types={['Epic']}
          title="Epic pipeline"
          badge="1 type uses this"
          stages={epic.stages}
        />
      ) : (
        <Banner kind="default">Epic pipeline not configured.</Banner>
      )}

      {shared ? (
        <PipelineCard
          testId="settings-work-items-pipeline-shared"
          types={bugSplit ? ['Story', 'Task'] : ['Story', 'Bug', 'Task']}
          title="Shared pipeline (Story / Bug / Task)"
          badge={bugSplit ? '2 types use this' : '3 types use this'}
          stages={shared.stages}
          footer={
            bugSplit ? null : (
              <div className="set-wi-split-row">
                <button
                  type="button"
                  className="set-wi-split-btn"
                  data-testid="settings-work-items-split-bug"
                  onClick={onSplit}
                  disabled={splitPending}
                >
                  ⎘ Split Bug into its own pipeline
                </button>
                <p className="set-wi-split-note">
                  Useful if Bugs need <code>Triaged / Confirmed / Verified</code>.
                  Costly because Bugs no longer share columns with Stories on the
                  workspace board.
                </p>
              </div>
            )
          }
        />
      ) : (
        <Banner kind="default">Shared pipeline not configured.</Banner>
      )}

      {bug ? (
        <PipelineCard
          testId="settings-work-items-pipeline-bug"
          types={['Bug']}
          title="Bug pipeline"
          badge="1 type uses this"
          stages={bug.stages}
          headerExtra={
            <button
              type="button"
              className="set-wi-split-btn set-wi-split-btn--head"
              data-testid="settings-work-items-merge-bug"
              onClick={onMerge}
              disabled={splitPending}
            >
              ⎌ Re-merge into Shared
            </button>
          }
        />
      ) : null}

      {subtask ? (
        <PipelineCard
          testId="settings-work-items-pipeline-subtask"
          types={['Subtask']}
          title="Subtask pipeline"
          badge="fixed — not editable"
          stages={subtask.stages}
          fixed
          footer={
            <p className="set-wi-fixed-note">
              Subtasks are checklist-grade. Custom stages here create noise
              without value.
            </p>
          }
        />
      ) : null}

      <p className="set-wi-doc-note" data-testid="settings-work-items-doc-note">
        <strong>Documents</strong> don't appear on this tab. Their lifecycle
        (Draft → Published → Archived) is system-defined and intentionally not
        org-configurable — the moment an org can rename "Published" to "In
        Review → Approved → Shipped," they've turned Documents into Work Items
        and the wall collapses.
      </p>
    </div>
  );
}

function PipelineCard({
  testId,
  types,
  title,
  badge,
  stages,
  footer,
  headerExtra,
  fixed
}: {
  testId: string;
  types: string[];
  title: string;
  badge: string;
  stages: string[];
  footer?: React.ReactNode;
  headerExtra?: React.ReactNode;
  fixed?: boolean;
}) {
  return (
    <article
      className={
        fixed ? 'set-wi-pcard set-wi-pcard--fixed' : 'set-wi-pcard'
      }
      data-testid={testId}
    >
      <header className="set-wi-pcard-head">
        {types.map((t) => (
          <span key={t} className="set-wi-type-pill">
            {t}
          </span>
        ))}
        <span className="set-wi-pcard-title">{title}</span>
        <span className="set-wi-pcard-badge" data-testid={`${testId}-badge`}>
          {badge}
        </span>
        {headerExtra}
      </header>
      <div className="set-wi-stages" data-testid={`${testId}-stages`}>
        {stages.map((s, i) => (
          <span key={`${s}-${i}`} className="set-wi-stage">
            {s}
          </span>
        ))}
      </div>
      {footer}
    </article>
  );
}

function PropertyLibraryPane({
  propertyDefs,
  optionsByDefId,
  addPending,
  onAdd
}: {
  propertyDefs: ReadonlyArray<PropertyDefRow>;
  optionsByDefId: Map<string, PropertyOptionRow[]>;
  addPending: boolean;
  onAdd: () => void;
}) {
  return (
    <div
      className="set-wi-pane set-wi-pane--props"
      data-testid="settings-work-items-pane-property-library"
    >
      <header className="set-wi-props-head">
        <h2 className="set-wi-props-title">Work item property library</h2>
        <span className="set-wi-props-helper">
          Workspace-scoped · never mixes with the Document library
        </span>
        <button
          type="button"
          className="set-wi-props-new"
          data-testid="settings-work-items-add-property"
          onClick={onAdd}
          disabled={addPending}
        >
          + New property
        </button>
      </header>
      <table
        className="set-wi-props-table"
        data-testid="settings-work-items-property-table"
      >
        <thead>
          <tr>
            <th className="set-wi-props-col-name">Name</th>
            <th>Type</th>
            <th>Options</th>
            <th>Applies to</th>
            <th aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {propertyDefs.length === 0 ? (
            <tr>
              <td
                colSpan={5}
                className="set-wi-empty"
                data-testid="settings-work-items-property-table-empty"
              >
                No properties yet. Click + New property to add one.
              </td>
            </tr>
          ) : (
            propertyDefs.map((p) => {
              const opts = optionsByDefId.get(p.id) ?? [];
              const optionsCell =
                p.kind === 'single_select' || p.kind === 'multi_select'
                  ? opts.length === 0
                    ? '—'
                    : opts.map((o) => o.label).join(' · ')
                  : '—';
              const appliesCell =
                p.type_id === null
                  ? 'All types'
                  : `Type ${p.type_id.slice(0, 8)}`;
              return (
                <tr
                  key={p.id}
                  data-testid={`settings-work-items-property-row-${p.id}`}
                  data-applies-to={p.type_id ?? 'all'}
                >
                  <td>{p.label}</td>
                  <td>
                    <span className="set-wi-props-type">{p.kind}</span>
                  </td>
                  <td>{optionsCell}</td>
                  <td>
                    <span className="set-wi-props-applies">{appliesCell}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="set-wi-props-edit"
                      data-testid={`settings-work-items-property-edit-${p.id}`}
                      disabled
                      title="Editor lands in a later turn"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <p
        className="set-wi-props-wall"
        data-testid="settings-work-items-property-wall"
      >
        <strong>The wall.</strong> If your org wants <code>Tags</code> on both
        Work Items AND Documents, define it twice — once here, once in the
        Document property library. The wall keeps "is this a Document or a Work
        Item?" unambiguous.
      </p>
    </div>
  );
}
